import { ForceAgreement, BaseAgreement, AgreementActionEnum } from '@hb/coffee-widgets-agreement';
import { IS_PRERENDERING } from '@hb/coffee-prerender';
import type { ForceReadSceneType } from '@hb/coffee-widgets-agreement';
import {
  contractReadSave, queryIsRead,
} from '@/api/rights/contract';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { ubtSendError } from '@/shared/utils/ubt';
import { RightPackageTypeEnum } from '@/api/rights/types';
import type { ForceReadOptionsType, ContractItem } from './types';
import { ContractReadStatusEnum } from './enum';
import { ContractService } from './contractService';

const EventTrackingID = { Contract_ForceRead: 'finance_member_contract_forceRead' };

/**
 * 组合式函数：强制阅读协议处理逻辑
 * 提供强制阅读协议的状态管理、数据初始化和交互处理
 */
export const ForceReadService = (
  opts:{
  packageType: RightPackageTypeEnum,
  forceReadFromScene: string,
},
): {
  readStatus: ContractReadStatusEnum,
  handleForceRead: (opt: ForceReadOptionsType) => Promise<boolean>,
  destroyInstance: (
)=> void
} => {
  // 1.状态聚合
  const state: {
    readStatus: ContractReadStatusEnum,
    contractOption: ContractItem[],
    needRead: boolean,
    tqParams: Dictionary
  } = {
    /**
     * 强制阅读状态标识
     * - 1表示初始化默认状态
     */
    readStatus: ContractReadStatusEnum.INIT,
    /** 当前是否需要强制阅读的状态标识 */
    needRead: false,
    /** 传递给协议组件的合同列表数据 */
    contractOption: [],
    tqParams: {},
  };
  // 新增初始化状态管理
  let initPromise: Promise<void> | undefined;

  const { sceneCache, loadContracts } = ContractService();

  /**
   * 显示强制阅读弹窗组件
   * @param forceReadScene - 强制阅读场景配置数组
   * @returns Promise解析为用户的交互动作结果
   */
  const showForceReadPop = async (
    forceReadScene?: ForceReadSceneType,
  ) => ForceAgreement.show({
    title: '会员综合协议',
    contractOption: state.contractOption,
    forceReadScene,
    buttonTextGroup: ['滑动到底部', '已阅读并同意相关协议'],
    zIndex: 2050,
  });


  /**
   * 获取合同列表数据（带缓存机制）
   * @param scene - 合同场景标识
   * @remarks
   * 1. 优先从缓存获取数据
   * 2. 无缓存时发起网络请求
   * 3. 处理PDF预览地址拼接
   * 4. 错误处理及埋点上报
   */
  const getContractListApi = async (scene: string, rightsModelType?: number) => {
    // 再次打开协议阅读场景时，从缓存中获取，防止重复请求
    if (sceneCache.has(scene)) {
      const cached = sceneCache.get(scene);
      if (cached) {
        state.contractOption = cached;
        return;
      }
    }

    try {
      const contractList = await loadContracts(scene, rightsModelType) || [];
      const validList = contractList.filter((item): item is ContractItem => !!item);
      if (validList.length === 0) {
        sceneCache.delete(scene);
        throw new Error('Empty contract list');
      }
      sceneCache.set(scene, validList); // 缓存结果
      state.contractOption = validList;
    } catch (e) {
      ubtSendError({
        name: 'contract_list_error',
        message: `Failed to get list for scene ${scene}`,
        stack: e instanceof Error ? e.stack || String(e) : String(e),
      });
      sceneCache.delete(scene);
      throw e;
    }
  };

  /**
   * 执行强制阅读流程
   * @param packageType - 权益包类型
   * @param scene - 合同场景标识
   * @returns Promise解析为是否跳过后续流程：
   * - true: 需要中断流程
   * - false: 继续执行后续流程
   * @remarks 包含埋点上报和阅读状态保存
   */
  const forceRead = async (
    packageType: RightPackageTypeEnum,
    scene: string,
    rightsModelType?: number,
  ):Promise<boolean> => {
    await getContractListApi(scene, rightsModelType);

    // 判断调用强制阅读组件处理强制阅读组件相关挂载
    try {
      const action = await showForceReadPop(['SCROLL_TO_BOTTOM']);
      if (!action) return true;

      if (action) {
        // 埋点
        ubtSetupPageButton(
          EventTrackingID.Contract_ForceRead,
          { action, ...state.tqParams },
        );
        // 如果是点击确认，则调用强制阅读协议接口
        if (action === AgreementActionEnum.CONFIRM) {
          // 调用强制阅读保存接口，保证最终即使接口调用错误也能正常执行下方流程
          try {
            await contractReadSave({ packageType });
            return true;
          } catch {
            return true;
          }
        }
      }

      return action !== AgreementActionEnum.CLOSED;
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      ubtSetupPageButton(
        EventTrackingID.Contract_ForceRead,
        { action: error.message },
      );
      ubtSendError({
        name: 'right_force_read',
        message: 'forceRead handle error',
        stack: error.stack || String(error),
      });
      return true;
    }
  };

  /**
   * 合同预览功能
   * @param scene - 合同场景标识
   * @remarks 使用基础协议组件展示合同内容
   */
  const previewContract = async (scene: string, rightsModelType?: number) => {
    await getContractListApi(scene, rightsModelType);
    await BaseAgreement.show({
      title: '会员综合协议',
      contractOption: state.contractOption,
      zIndex: 2050,
    });
  };

  /**
   * 初始化阅读状态数据
   * @param packageType - 权益包类型
   * @param scene - 场景标识
   * @throws {Error} 当接口请求失败时抛出
  */
  const initData = async () => {
    const { packageType, forceReadFromScene } = opts;
    try {
      state.needRead = await queryIsRead({
        packageType,
        sceneCode: forceReadFromScene,
      });
    } catch (e) {
      ubtSendError({
        name: 'init_data_error',
        message: `Failed to init data for ${opts.packageType}`,
        stack: String(e),
      });
      state.needRead = false;
    }
  };

  /**
   * 初始化数据并返回持久化的Promise实例
   *
   * 该函数通过维护一个全局Promise实例确保初始化逻辑的幂等性：
   * 1. 首次调用时创建初始化Promise
   * 2. 后续调用直接返回已存在的Promise
   * @returns {Promise} 始终返回同一个初始化操作的Promise实例
   */
  const initDataWithPromise = async () => {
    if (!initPromise) {
      initPromise = initData();
    }
    return initPromise;
  };

  /**
   * 处理强制阅读主流程
   * @param opt - 强制阅读配置选项
   * @returns Promise解析为流程处理结果：
   * - true: 继续执行后续流程
   * - false: 需要中断流程
   * @remarks 处理三种情况：
   * 1. 需要强制阅读,弹出强制阅读弹窗，阻断后续流程
   * 2. 无需强制阅读,直接执行后续流程
   * 3. 无需强制阅读,但是需要预览合同的场景，弹出预览合同
   */
  const handleForceRead = async (
    opt: ForceReadOptionsType,
  ): Promise<boolean> => {
    const {
      contractScene,
      preview,
      tqParams = {},
      rightsModelType,
    } = opt;

    const { packageType } = opts;
    state.tqParams = tqParams;

    // 确认初始化完成
    await initDataWithPromise();
    // 如果需要强制阅读则直接强制阅读
    if (state.needRead) {
      ubtSetupModuleExpose(
        EventTrackingID.Contract_ForceRead,
        { ...tqParams },
      );
      const handleForceReadRes = await forceRead(packageType, contractScene, rightsModelType);
      // 此处不论成功与否都将状态置为false
      state.needRead = !handleForceReadRes;
      // 根据强制阅读处理结果来更新状态
      state.readStatus = handleForceReadRes
        ? ContractReadStatusEnum.NEED_READ_HAS_READ : ContractReadStatusEnum.NEED_READ_NOT_READ;

      return handleForceReadRes;
    }

    state.readStatus = ContractReadStatusEnum.NOT_NEED_READ;
    // 如果是需要预览合同，则直接预览合同
    if (preview) {
      await previewContract(contractScene, rightsModelType);
      return false;
    }

    return true;
  };

  const destroyInstance = () => {
    initPromise = undefined;
    state.readStatus = ContractReadStatusEnum.INIT;
    BaseAgreement.clear();
    ForceAgreement.clear();
  };

  /** 非预渲染的情况下执行 */
  if (!IS_PRERENDERING) {
    initDataWithPromise();
  }

  return {
    readStatus: state.readStatus,
    handleForceRead,
    destroyInstance,
  };
};

export type ForceReadServiceReturnType = ReturnType<typeof ForceReadService>;
